/**
 * Presentation helpers for the holiday screens over the API's shapes (@aroo/shared holidays).
 * The balance itself is computed by the API (one engine for every screen); nothing here adds
 * numbers up except the live "days this request would use" preview, which mirrors the API's
 * countDays rule over the person's weekly pattern.
 */
import {
  HOLIDAY_STATUS_LABELS,
  WEEKDAYS,
  WEEKDAY_LABELS,
  type HolidayBalance,
  type HolidayBooking,
  type HolidayBookingStatus,
  type WeeklyPattern,
} from '@aroo/shared';
import type { PillTone } from '../../components/StatusPill';
import { EMPTY, formatDateRange as formatRange, formatDayMonth } from '../../lib/format';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Local-time Date for an ISO date (so weekday maths matches the calendar the user sees). */
export function isoToLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function localDateToIso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "Mon 19 Oct"; "Mon 2 Dec 2024" when it isn't this year (stale requests read honestly). */
export function formatDay(iso: string, now: Date = new Date()): string {
  return formatDayMonth(iso, EMPTY, now);
}

/**
 * "Mon 19 – Fri 23 Oct", "Mon 19 Oct", "Fri 30 Oct – Mon 2 Nov"; with years when either end
 * isn't this year ("Tue 29 Dec 2026 – Sat 2 Jan 2027").
 */
export function formatDateRange(startIso: string, endIso: string, now: Date = new Date()): string {
  const y = now.getFullYear();
  const withYear = Number(startIso.slice(0, 4)) !== y || Number(endIso.slice(0, 4)) !== y;
  return formatRange(startIso, endIso, { withYear });
}

/** The booking's dates with the half-day suffix: "Mon 2 Nov, afternoon". */
export function bookingDates(
  b: Pick<HolidayBooking, 'startDate' | 'endDate' | 'half'>,
  now: Date = new Date(),
): string {
  const range = formatDateRange(b.startDate, b.endDate, now);
  if (b.half === 'am') return `${range}, morning`;
  if (b.half === 'pm') return `${range}, afternoon`;
  return range;
}

/** Saturday or Sunday (the calendar greys weekends; the pattern decides what counts). */
export function isWeekend(d: Date): boolean {
  const dow = d.getDay();
  return dow === 0 || dow === 6;
}

/** Is a weekday a working day under the pattern? Date is local time. */
export function isWorkingDay(pattern: WeeklyPattern, d: Date): boolean {
  const idx = (d.getDay() + 6) % 7; // Monday first
  return pattern[WEEKDAYS[idx] ?? 'mon'];
}

/**
 * Days a request would use over one fixed week pattern (the API's countDays rule): working
 * days in the range that are not auto-booked bank holidays, and a single-day half = 0.5 even
 * on a day off (M2b). Screens with the full model use `requestedDaysFor` in workingDays.ts.
 */
export function requestedDays(
  start: Date,
  end: Date,
  pattern: WeeklyPattern,
  halfDay: boolean,
  autoBookedBankHolidays: ReadonlySet<string> = new Set(),
): number {
  if (end.getTime() < start.getTime()) return 0;
  if (halfDay && start.getTime() === end.getTime()) {
    return autoBookedBankHolidays.has(localDateToIso(start)) ? 0 : 0.5;
  }
  let n = 0;
  for (let t = new Date(start); t.getTime() <= end.getTime(); t = new Date(t.getTime() + DAY_MS)) {
    if (isWorkingDay(pattern, t) && !autoBookedBankHolidays.has(localDateToIso(t))) n += 1;
  }
  return n;
}

/** "5 days", "1 day", "Half a day", "2.5 days", "−1 day". */
export function daysLabel(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  if (abs === 1) return `${sign}1 day`;
  if (abs === 0.5 && n > 0) return 'Half a day';
  return `${sign}${abs} days`;
}

/**
 * A booking's length: "3 days", "Half a day (morning)", or "Not counted" when none of its days
 * come off the balance (a half day on a day off showed "0 days").
 */
export function bookingDaysLabel(b: { days: number; half: 'am' | 'pm' | null }): string {
  if (b.days === 0) return b.half ? 'Half day, not counted' : 'Not counted';
  if (b.half && b.days === 0.5) return `Half a day (${b.half === 'am' ? 'morning' : 'afternoon'})`;
  return daysLabel(b.days);
}

/** Summary line under the date picker; `over` is true when the request exceeds what's left. */
export function afterLabel(remaining: number, requested: number): { text: string; over: boolean } {
  const after = remaining - requested;
  if (after < 0) {
    return {
      text: `That's ${-after} more than you have left. Pick fewer days or ask HR about an adjustment.`,
      over: true,
    };
  }
  return {
    text: `You'll have ${after} day${after === 1 ? '' : 's'} left after this.`,
    over: false,
  };
}

export const STATUS_TONE: Record<HolidayBookingStatus, PillTone> = {
  pending: 'warn',
  approved: 'info',
  declined: 'danger',
  cancelled: 'muted',
  withdrawn: 'muted',
};

/** The pill for a booking as of `today`: approved bookings in the past read "Taken". */
export function bookingPill(
  b: Pick<HolidayBooking, 'status' | 'endDate' | 'kind'>,
  today: string,
): { label: string; tone: PillTone } {
  if (b.status === 'approved' && b.endDate < today) return { label: 'Taken', tone: 'muted' };
  if (b.status === 'approved' && b.kind === 'bank_holiday_auto')
    return { label: 'Bank holiday', tone: 'info' };
  return { label: HOLIDAY_STATUS_LABELS[b.status], tone: STATUS_TONE[b.status] };
}

/** "Approved by Dean Brown", "Declined by …: reason", "Imported from the old system". */
export function bookingMeta(b: HolidayBooking): string {
  const parts: string[] = [];
  if (b.kind === 'bank_holiday_auto') {
    parts.push(
      b.bankHolidayName ? `${b.bankHolidayName} · booked automatically` : 'Booked automatically',
    );
  } else if (b.status === 'approved' || b.status === 'cancelled') {
    parts.push(
      b.decidedBy
        ? `Approved by ${b.decidedBy.name}`
        : b.imported
          ? 'Approved in the old system'
          : 'Approved',
    );
  } else if (b.status === 'declined') {
    parts.push(b.decidedBy ? `Declined by ${b.decidedBy.name}` : 'Declined');
    if (b.decisionNote) parts.push(b.decisionNote);
  } else if (b.status === 'pending') {
    parts.push('Waiting for a decision');
  } else {
    parts.push('Withdrawn');
  }
  if (b.status === 'cancelled')
    parts.push(b.cancelReason ? `Cancelled: ${b.cancelReason}` : 'Cancelled');
  if (b.note && b.kind !== 'bank_holiday_auto') parts.push(`“${b.note}”`);
  return parts.join(' · ');
}

/** "Mon–Fri", "Mon–Sat", "Mon, Wed, Fri", "Every day", "No working days". */
export function patternLabel(pattern: WeeklyPattern): string {
  const on = WEEKDAYS.filter((d) => pattern[d]);
  if (on.length === 0) return 'No working days';
  if (on.length === 7) return 'Every day';
  const first = WEEKDAYS.indexOf(on[0]);
  const last = WEEKDAYS.indexOf(on[on.length - 1]);
  const contiguous = last - first + 1 === on.length;
  if (contiguous && on.length > 2)
    return `${WEEKDAY_LABELS[on[0]]}–${WEEKDAY_LABELS[on[on.length - 1]]}`;
  return on.map((d) => WEEKDAY_LABELS[d]).join(', ');
}

/** The visible arithmetic: "28 + 3 − 11 − 4 − 2 = 14". */
export function arithmeticLabel(balance: HolidayBalance): string {
  const terms = balance.lines.map((l, i) => {
    const abs = Math.abs(l.days);
    if (i === 0) return String(l.days);
    return `${l.days < 0 ? '−' : '+'} ${abs}`;
  });
  return `${terms.join(' ')} = ${balance.remaining}`;
}

/** Bars on the balance card: proportions of the entitlement, clamped so a negative balance never overflows. */
export function barWidths(
  balance: HolidayBalance,
  extra = 0,
): { taken: string; booked: string; extra: string } {
  const base = Math.max(balance.entitled, 1);
  const pct = (n: number) => `${Math.max(0, Math.min(100, (n / base) * 100))}%`;
  return {
    taken: pct(balance.taken),
    booked: pct(balance.booked + balance.pending),
    extra: pct(Math.max(0, Math.min(extra, balance.remaining))),
  };
}

/** True if two inclusive date ranges share at least one day. */
export function rangesOverlap(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean {
  return aStart.getTime() <= bEnd.getTime() && aEnd.getTime() >= bStart.getTime();
}
